import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { ContentType } from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { getConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getConfluenceContentAri.ts';
import { getUrlForContentType } from '@confluence/content-types-utils/entry-points/getUrlForContentType.ts';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import {
	useFullPageEditorApiState,
	type FullPageEditorPresetAPI,
} from '@confluence/full-page-editor-store';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import {
	useLivePageMode,
	useLivePageStoreActions,
} from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useContentType, usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT } from './analytics.ts';
import { isViewModeSuggestionsEnabled } from './isViewModeSuggestionsEnabled.ts';
import {
	cancelLiveDocEditModeSelection,
	selectSuggestionInLiveDocEditMode,
} from './live/liveDocEditModeSelection.ts';
import {
	getPublishedSuggestionsSnapshot,
	requestPublishedReviewInEditMode,
	selectPublishedSuggestion,
	startPublishedSuggestions,
} from './published/publishedSuggestionsStore.ts';
import { usePublishedSuggestionsState } from './published/usePublishedSuggestionsState.ts';
import { getVisibleSuggestions } from './suggestionsPanelFilters.ts';
import { useSuggestionsPanelFilters } from './useSuggestionsPanelFilters.ts';

type AISuggestionsSharedState = NonNullable<
	ReturnType<NonNullable<FullPageEditorPresetAPI['aiSuggestions']>['sharedState']['currentState']>
>;

/**
 * Where the panel reads suggestions from:
 * - `editor`: the editor, where suggestions are acted on.
 * - `liveDocView`: the live doc's editor while it's in view mode. Suggestions are listed, and acted
 *   on by switching the live doc to edit mode.
 * - `published`: suggestions fetched for a published page, which has no editor. Suggestions are
 *   listed, and acted on by loading the editor.
 */
export type SuggestionsSourceMode = 'editor' | 'liveDocView' | 'published';

export type SuggestionsThinkingState = {
	status: AISuggestionsSharedState['thinkingStatus'] | undefined;
	startedAt: number | null;
	progressTarget: AISuggestionsSharedState['thinkingProgressTarget'] | null;
	agentReadyAt: number | null;
	reviewingAgent: NonNullable<AISuggestionsSharedState['reviewingAgent']> | undefined;
	regeneratingSuggestionIds: AISuggestionsSharedState['regeneratingSuggestionIds'] | undefined;
};

/** Panel actions in view mode, where suggestions are acted on by heading to edit mode. */
export type ViewModeActions = {
	isHeadingToEditMode: boolean;
	openInEditMode: (suggestionId?: string) => void;
	startReviewInEditMode: () => void;
};

type SuggestionsSourceBase = {
	count: number;
	suggestions: Suggestion[];
	visibleSuggestions: Suggestion[];
	selectedSuggestionId: string | undefined;
	selectSuggestion: (suggestionId: string | undefined) => void;
	isThinking: boolean;
	thinking: SuggestionsThinkingState | undefined;
	isInitialising: boolean;
	hasError: boolean;
};

export type SuggestionsSource =
	| (SuggestionsSourceBase & {
			mode: 'editor';
			editorAPI: FullPageEditorPresetAPI | undefined;
			viewModeActions?: never;
	  })
	| (SuggestionsSourceBase & {
			mode: 'liveDocView';
			editorAPI: FullPageEditorPresetAPI | undefined;
			viewModeActions: ViewModeActions;
	  })
	| (SuggestionsSourceBase & {
			mode: 'published';
			editorAPI?: never;
			viewModeActions: ViewModeActions;
	  });

export type SuggestionsSourceFor<M extends SuggestionsSourceMode> = Extract<
	SuggestionsSource,
	{ mode: M }
>;

const NO_SUGGESTIONS: Suggestion[] = [];

const getOrderedSuggestions = (aiSuggestionsState: AISuggestionsSharedState): Suggestion[] => {
	const suggestions: Suggestion[] = [];
	for (const id of aiSuggestionsState.orderedSuggestionIds) {
		const suggestion = aiSuggestionsState.suggestionById.get(id);
		if (suggestion !== undefined) {
			suggestions.push(suggestion);
		}
	}
	return suggestions;
};

type EditorViewModeState = { mode?: 'view' | 'edit' };

const selectEditorState = ({
	aiSuggestionsState,
	editorViewModeState,
}: {
	aiSuggestionsState?: AISuggestionsSharedState;
	editorViewModeState?: EditorViewModeState;
}) => ({
	// A live doc's editor reaches edit mode after the doc does. Until it has, suggestions
	// can't be acted on yet, so the panel stays in view mode as it heads there.
	isEditorInViewMode: editorViewModeState?.mode === 'view',
	suggestions: aiSuggestionsState ? getOrderedSuggestions(aiSuggestionsState) : [],
	selectedSuggestionId: aiSuggestionsState?.selectedSuggestion,
	thinkingStatus: aiSuggestionsState?.thinkingStatus,
	// Shared thinking-session anchor so the inline bar stays in sync with
	// the editor's thinking bar across mount/unmount.
	thinkingStartedAt: aiSuggestionsState?.thinkingStartedAt ?? null,
	thinkingProgressTarget: aiSuggestionsState?.thinkingProgressTarget ?? null,
	// When the reviewing agent became available in this session; anchors the loading
	// copy's rotation (see `loadingRotationStartedAt`).
	thinkingAgentReadyAt: aiSuggestionsState?.thinkingAgentReadyAt ?? null,
	// The full agent, for its avatar in the inline loading bar. Returned as the
	// plugin's object so the shallow comparison only changes when the agent does.
	reviewingAgent: aiSuggestionsState?.reviewingAgent ?? undefined,
	// Returned as the Set itself rather than a derived array — the hook
	// compares selector results shallowly, and this identity only changes
	// when the plugin swaps the set.
	regeneratingSuggestionIds: aiSuggestionsState?.regeneratingSuggestionIds,
});

type EditorState = ReturnType<typeof selectEditorState>;

/**
 * The selector hears of the editor's changes a notification after they're made. Leaving published
 * mode can happen in between, as the edit handoff ends when the editor's suggestions arrive, and the
 * panel would briefly show its empty state. Until the selector catches up, read the editor directly.
 */
const getLatestEditorState = (
	editorAPI: FullPageEditorPresetAPI | undefined,
	selectedState: EditorState,
): EditorState => {
	if (selectedState?.suggestions?.length) {
		return selectedState;
	}
	const aiSuggestionsState = editorAPI?.aiSuggestions?.sharedState?.currentState();
	if (!aiSuggestionsState?.orderedSuggestionIds.length || !isViewModeSuggestionsEnabled()) {
		return selectedState;
	}
	return selectEditorState({
		aiSuggestionsState,
		editorViewModeState: editorAPI?.editorViewMode?.sharedState.currentState() ?? undefined,
	});
};

/**
 * Whether the panel lists published suggestions: they were requested for some content, or there is
 * no editor to read suggestions from.
 */
export const isPublishedSuggestionsMode = (
	publishedContentId: string | undefined,
	editorAPI: FullPageEditorPresetAPI | undefined,
): boolean => (publishedContentId !== undefined || !editorAPI) && isViewModeSuggestionsEnabled();

/** Which suggestions the panel lists, and so how they can be acted on. */
export const getSuggestionsSourceMode = ({
	publishedContentId,
	editorAPI,
	isLivePage,
	isLivePageInViewMode,
}: {
	publishedContentId: string | undefined;
	editorAPI: FullPageEditorPresetAPI | undefined;
	isLivePage: boolean;
	isLivePageInViewMode: boolean;
}): SuggestionsSourceMode => {
	if (isPublishedSuggestionsMode(publishedContentId, editorAPI)) {
		return 'published';
	}
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- The gates only apply to live docs in view mode.
	if (isLivePage && isLivePageInViewMode && isViewModeSuggestionsEnabled()) {
		return 'liveDocView';
	}
	return 'editor';
};

type UseSuggestionsSourceOptions = {
	/**
	 * Called when the panel switches a live doc to edit mode, to announce it as the mode switcher
	 * does. Only the panel passes it, as the header and count never switch modes.
	 */
	onSwitchLiveDocToEditMode?: () => void;
};

/**
 * The suggestions the panel lists, and how to act on them. Shared by the panel body, footer and
 * header, so each call reads the same state; its effects are safe to run from every caller.
 */
export const useSuggestionsSource = ({
	onSwitchLiveDocToEditMode,
}: UseSuggestionsSourceOptions = {}): SuggestionsSource => {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const publishedState = usePublishedSuggestionsState();
	const isLivePage = useIsCurrentPageLive();
	const [{ isViewMode: isLivePageInViewMode }] = useLivePageMode();
	const { setMode: setLivePageMode } = useLivePageStoreActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ currentFilter, currentSort }] = useSuggestionsPanelFilters();
	const { activationId, cloudId, userId } = useSessionData();
	const [pageContentId] = usePageContentId();
	const [contentType] = useContentType();
	const [spaceKey = ''] = usePageSpaceKey();
	const isEditorPage = useIsEditorPage();
	const { loadEditor } = useEditPageLoadingActions();

	// Read from the editor whatever the mode, as the mode depends on the editor's view mode. Its
	// suggestions are only listed outside published mode.
	const selectedEditorState = useSharedPluginStateWithSelector(
		fullPageEditorPresetAPI,
		['aiSuggestions', 'editorViewMode'],
		selectEditorState,
	);
	const editorState = getLatestEditorState(fullPageEditorPresetAPI, selectedEditorState);

	const mode = getSuggestionsSourceMode({
		publishedContentId: publishedState.contentId,
		editorAPI: fullPageEditorPresetAPI,
		isLivePage,
		isLivePageInViewMode: isLivePageInViewMode || editorState?.isEditorInViewMode === true,
	});
	const editorAPI = mode === 'published' ? undefined : fullPageEditorPresetAPI;

	// Selecting through the editor would show the diff, so live doc view mode selects here.
	const [liveDocViewSelectedSuggestionId, setLiveDocViewSelectedSuggestionId] = useState<
		string | undefined
	>();
	const [isHeadingToEditMode, setIsHeadingToEditMode] = useState(false);
	// Heading to edit mode, and what was selected on the way, only applies to the mode it began in.
	// The panel can stay in live doc view mode while heading to edit mode, so the doc being back in
	// view mode also ends it.
	const isLiveDocInViewMode = mode === 'liveDocView' && isLivePageInViewMode;
	const [previousMode, setPreviousMode] = useState(mode);
	const [wasLiveDocInViewMode, setWasLiveDocInViewMode] = useState(isLiveDocInViewMode);
	if (previousMode !== mode || wasLiveDocInViewMode !== isLiveDocInViewMode) {
		if (previousMode !== mode || isLiveDocInViewMode) {
			setLiveDocViewSelectedSuggestionId(undefined);
			setIsHeadingToEditMode(false);
		}
		setPreviousMode(mode);
		setWasLiveDocInViewMode(isLiveDocInViewMode);
	}

	// Back in view mode before the editor reached edit mode, so the viewed suggestion is stale.
	const wasLiveDocInViewModeRef = useRef(isLiveDocInViewMode);
	useEffect(() => {
		if (isLiveDocInViewMode && !wasLiveDocInViewModeRef.current) {
			cancelLiveDocEditModeSelection();
		}
		wasLiveDocInViewModeRef.current = isLiveDocInViewMode;
	}, [isLiveDocInViewMode]);

	// Load the published page's suggestions. Every caller runs this, so it reads the store rather
	// than its render's snapshot to start the load once.
	useEffect(() => {
		if (
			mode === 'published' &&
			!isEditorPage &&
			pageContentId &&
			getPublishedSuggestionsSnapshot().contentId !== pageContentId
		) {
			startPublishedSuggestions(
				pageContentId,
				getConfluenceContentAri({
					siteId: cloudId,
					resourceType: contentType ?? ContentType.PAGE,
					activationId,
					resourceId: pageContentId,
				}),
			);
		}
	}, [activationId, cloudId, contentType, isEditorPage, mode, pageContentId]);

	const selectSuggestion = useCallback(
		(suggestionId: string | undefined) => {
			if (mode === 'published') {
				selectPublishedSuggestion(suggestionId);
				return;
			}
			if (mode === 'liveDocView') {
				setLiveDocViewSelectedSuggestionId(suggestionId);
				return;
			}
			editorAPI?.aiSuggestions?.actions.selectSuggestion(suggestionId ?? null, {
				interactionPoint: 'sidebar',
			});
		},
		[editorAPI, mode],
	);

	// A published page is edited by loading the editor for it.
	const publishedContentId = publishedState.contentId;
	const openPublishedInEditMode = useCallback(
		(suggestionId?: string) => {
			setIsHeadingToEditMode(true);
			if (!publishedContentId || isEditorPage) {
				return;
			}
			if (suggestionId) {
				selectPublishedSuggestion(suggestionId);
			}
			loadEditor({
				contentId: publishedContentId,
				spaceKey,
				contentType,
				redirectUrl: getUrlForContentType({
					contentType,
					spaceKey,
					contentId: publishedContentId,
					redirectToEditor: true,
				}),
			});
		},
		[contentType, isEditorPage, loadEditor, publishedContentId, spaceKey],
	);
	const startPublishedReviewInEditMode = useCallback(() => {
		requestPublishedReviewInEditMode();
		openPublishedInEditMode();
	}, [openPublishedInEditMode]);

	// A live doc is edited by switching its mode, announced the same way as the mode switcher. The
	// panel waits for the editor's edit mode only when the editor reports its mode.
	const switchLiveDocToEditMode = useCallback(() => {
		if (editorAPI?.editorViewMode) {
			setIsHeadingToEditMode(true);
		}
		onSwitchLiveDocToEditMode?.();
		void setLivePageMode('edit');
		createAnalyticsEvent?.({
			type: 'sendUIEvent',
			data: {
				source: 'suggestionsPanel',
				action: 'changed',
				actionSubject: 'mode',
				actionSubjectId: 'livePagesModeSwitcher',
				attributes: { previousMode: 'view', newMode: 'edit' },
				objectType: 'page',
				objectId: pageContentId,
			},
		})?.fire();
	}, [createAnalyticsEvent, editorAPI, onSwitchLiveDocToEditMode, pageContentId, setLivePageMode]);
	const openLiveDocInEditMode = useCallback(
		(suggestionId?: string) => {
			if (suggestionId) {
				setLiveDocViewSelectedSuggestionId(suggestionId);
				selectSuggestionInLiveDocEditMode(editorAPI, suggestionId);
			}
			switchLiveDocToEditMode();
		},
		[editorAPI, switchLiveDocToEditMode],
	);
	const startLiveDocReviewInEditMode = useCallback(() => {
		switchLiveDocToEditMode();
		editorAPI?.aiSuggestions?.actions.setThinkingState('thinking', {
			entryPoint: SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT,
		});
	}, [editorAPI, switchLiveDocToEditMode]);

	const isPublishedHeadingToEditMode = isHeadingToEditMode || isEditorPage;
	const publishedViewModeActions = useMemo<ViewModeActions>(
		() => ({
			isHeadingToEditMode: isPublishedHeadingToEditMode,
			openInEditMode: openPublishedInEditMode,
			startReviewInEditMode: startPublishedReviewInEditMode,
		}),
		[isPublishedHeadingToEditMode, openPublishedInEditMode, startPublishedReviewInEditMode],
	);
	const liveDocViewModeActions = useMemo<ViewModeActions>(
		() => ({
			isHeadingToEditMode,
			openInEditMode: openLiveDocInEditMode,
			startReviewInEditMode: startLiveDocReviewInEditMode,
		}),
		[isHeadingToEditMode, openLiveDocInEditMode, startLiveDocReviewInEditMode],
	);

	const suggestions =
		mode === 'published'
			? publishedState.suggestions
			: (editorState?.suggestions ?? NO_SUGGESTIONS);
	const visibleSuggestions = useMemo(
		() =>
			getVisibleSuggestions(suggestions, { currentFilter, currentSort, currentUserId: userId }) ??
			[],
		[suggestions, currentFilter, currentSort, userId],
	);

	if (mode === 'published') {
		return {
			mode,
			count: suggestions.length,
			suggestions,
			visibleSuggestions,
			selectedSuggestionId: publishedState.selectedSuggestionId,
			selectSuggestion,
			isThinking: false,
			thinking: undefined,
			isInitialising: publishedState.status === 'idle' || publishedState.status === 'loading',
			hasError: publishedState.status === 'error',
			viewModeActions: publishedViewModeActions,
		};
	}

	const editorSourceBase: SuggestionsSourceBase = {
		count: suggestions.length,
		suggestions,
		visibleSuggestions,
		selectedSuggestionId:
			mode === 'liveDocView' ? liveDocViewSelectedSuggestionId : editorState.selectedSuggestionId,
		selectSuggestion,
		isThinking: editorState.thinkingStatus === 'thinking',
		thinking: {
			status: editorState.thinkingStatus,
			startedAt: editorState.thinkingStartedAt,
			progressTarget: editorState.thinkingProgressTarget,
			agentReadyAt: editorState.thinkingAgentReadyAt,
			reviewingAgent: editorState.reviewingAgent,
			regeneratingSuggestionIds: editorState.regeneratingSuggestionIds,
		},
		isInitialising: false,
		hasError: false,
	};

	if (mode === 'liveDocView') {
		return {
			...editorSourceBase,
			mode,
			editorAPI,
			viewModeActions: liveDocViewModeActions,
		};
	}

	return { ...editorSourceBase, mode, editorAPI };
};
